import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from "@mui/material";
import { withAuthenticationRequired } from "../context/AuthContext";
import Loading from "../components/Loading";
import { useRequestHandler } from "../utils/useRequestHandler";
import { showInfoMessage, showSuccessMessage } from "../redux/appSlice";
import Servicios from "../services";

// Evento.Tipo del backend.
export const TIPOS = {
  1: "Prueba de Admisión",
  2: "Desfile Inaugural",
  3: "Escuelas de Samba",
  4: "Llamadas"
};

// Ids de CategoriaConcurso (los mismos que usa el app).
export const CATEGORIAS = {
  1: "Murgas",
  2: "Parodistas",
  3: "Humoristas",
  4: "Revistas",
  5: "Comparsas",
  6: "Fuera de concurso"
};

const LUGAR_POR_TIPO = { 1: "Teatro de Verano", 2: "Avenida 18 de Julio", 3: "Avenida 18 de Julio", 4: "Barrio Sur y Palermo" };

const EVENTO_VACIO = {
  tipo: 1,
  anio: new Date().getFullYear() + 1,
  nombre: TIPOS[1],
  lugar: LUGAR_POR_TIPO[1],
  nota: "",
  fuenteUrl: "",
  fuenteFallosUrl: "",
  fechaFallos: null,
  publicado: false
};

const AYUDA_PROGRAMACION = `# Etapa 1 · 2026-11-10 · 20:30
20:30 La Malandra (Murga)
21:05 Desirée (Humoristas)
Carro oficial (no compite)`;

const AYUDA_FALLOS = `# Murgas · Clasificados
1. La Malandra 512,5
Inframurga, 498 puntos
# Premios
Mejor murga: La Malandra`;

const PUSH = {
  enviada: "Fallos publicados y push enviada",
  ya_enviada: "Fallos publicados. La push ya se había mandado: no se repitió",
  prueba: "Push de prueba enviada a los admins (los fallos no se publicaron)",
  no_solicitada: "Fallos publicados, sin push"
};

/**
 * Eventos de temporada: alta y edición, Publicado, y la carga de la
 * programación y los fallos pegando texto (Previsualizar → revisar → Guardar).
 * Los participantes son nombres con un vínculo opcional a un conjunto: nunca
 * se crean conjuntos desde acá.
 */
export const Eventos = () => {
  const sendRequest = useRequestHandler();
  const [eventos, setEventos] = useState(null);
  const [seleccionadoId, setSeleccionadoId] = useState(null);
  const [creando, setCreando] = useState(false);

  const cargar = () => sendRequest(Servicios.ObtenerEventosAdmin, [], r => setEventos(r?.data ?? []));

  useEffect(() => {
    cargar();
  }, []);

  const cambiarPublicado = (evento, publicado) =>
    sendRequest(Servicios.CambiarPublicadoEvento, [evento.id, publicado], () => cargar());

  return (
    <div className="card-modern">
      <div className="page-header flex items-center justify-between">
        <h4 className="page-title">Eventos de temporada</h4>
        <Button
          variant="contained"
          onClick={() => {
            setSeleccionadoId(null);
            setCreando(true);
          }}
        >
          Nuevo evento
        </Button>
      </div>
      <hr className="border-gray-200 dark:border-gray-700 my-4" />

      {eventos && eventos.length === 0 && <p>Todavía no hay eventos.</p>}
      {eventos && eventos.length > 0 && (
        <Table size="small" aria-label="Eventos">
          <TableHead>
            <TableRow>
              <TableCell>Edición</TableCell>
              <TableCell>Evento</TableCell>
              <TableCell>Fechas</TableCell>
              <TableCell>Programación</TableCell>
              <TableCell>Fallos</TableCell>
              <TableCell>Publicado</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {eventos.map(e => (
              <TableRow key={e.id} selected={e.id === seleccionadoId}>
                <TableCell>{e.anio}</TableCell>
                <TableCell>
                  {e.nombre}
                  <div className="text-xs text-gray-500">{e.lugar}</div>
                </TableCell>
                <TableCell>{e.desde ? `${e.desde} → ${e.hasta}` : "—"}</TableCell>
                <TableCell>
                  {e.noches} noches · {e.participaciones} participantes
                </TableCell>
                <TableCell>
                  {e.resultados} resultados · {e.premios} premios{" "}
                  {e.fallosPublicados && <Chip size="small" color="success" label="Publicados" />}
                  {e.fechaPushFallos && <Chip size="small" variant="outlined" label="Push enviada" />}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={e.publicado}
                    inputProps={{ "aria-label": `Publicado ${e.nombre} ${e.anio}` }}
                    onChange={ev => cambiarPublicado(e, ev.target.checked)}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    onClick={() => {
                      setCreando(false);
                      setSeleccionadoId(e.id);
                    }}
                  >
                    Editar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {creando && (
        <DatosEvento
          evento={EVENTO_VACIO}
          onGuardado={detalle => {
            setCreando(false);
            setSeleccionadoId(detalle.id);
            cargar();
          }}
          onCancelar={() => setCreando(false)}
        />
      )}
      {seleccionadoId && (
        <EditorEvento
          key={seleccionadoId}
          id={seleccionadoId}
          onCambio={cargar}
          onEliminado={() => {
            setSeleccionadoId(null);
            cargar();
          }}
        />
      )}
    </div>
  );
};

const EditorEvento = ({ id, onCambio, onEliminado }) => {
  const sendRequest = useRequestHandler();
  const [detalle, setDetalle] = useState(null);

  const cargar = () => sendRequest(Servicios.ObtenerEventoAdmin, [id], r => setDetalle(r?.data ?? null));

  useEffect(() => {
    cargar();
  }, [id]);

  if (!detalle) return null;

  const recargar = d => {
    if (d) setDetalle(d);
    else cargar();
    onCambio();
  };

  const eliminar = () => {
    if (!window.confirm(`¿Eliminar «${detalle.nombre} ${detalle.anio}» con su programación y sus fallos? No se puede deshacer.`)) return;
    sendRequest(Servicios.EliminarEvento, [id], () => onEliminado());
  };

  return (
    <div className="mt-6 flex flex-col gap-6">
      <DatosEvento evento={detalle} id={id} onGuardado={recargar} onEliminar={eliminar} />
      <Programacion detalle={detalle} onGuardado={recargar} />
      <Fallos detalle={detalle} onGuardado={recargar} />
    </div>
  );
};

const DatosEvento = ({ evento, id, onGuardado, onCancelar, onEliminar }) => {
  const sendRequest = useRequestHandler();
  const dispatch = useDispatch();
  const [datos, setDatos] = useState({ ...EVENTO_VACIO, ...evento });

  const set = campo => valor => setDatos(d => ({ ...d, [campo]: valor }));

  const cambiarTipo = tipo =>
    setDatos(d => ({
      ...d,
      tipo,
      nombre: id ? d.nombre : TIPOS[tipo],
      lugar: id ? d.lugar : LUGAR_POR_TIPO[tipo]
    }));

  const guardar = () => {
    const cuerpo = {
      tipo: Number(datos.tipo),
      anio: Number(datos.anio),
      nombre: datos.nombre,
      lugar: datos.lugar,
      nota: datos.nota || null,
      fuenteUrl: datos.fuenteUrl || null,
      fuenteFallosUrl: datos.fuenteFallosUrl || null,
      fechaFallos: datos.fechaFallos || null,
      publicado: !!datos.publicado
    };
    const llamada = id ? Servicios.ModificarEvento : Servicios.CrearEvento;
    const params = id ? [id, cuerpo] : [cuerpo];
    sendRequest(llamada, params, r => {
      dispatch(showSuccessMessage("Evento guardado"));
      onGuardado(r?.data);
    });
  };

  return (
    <section aria-label="Datos del evento" className="border border-gray-200 dark:border-gray-700 rounded p-4">
      <h5 className="page-title mb-3">{id ? `${evento.nombre} ${evento.anio}` : "Nuevo evento"}</h5>
      <div className="flex flex-wrap gap-3">
        <FormControl size="small" style={{ minWidth: 200 }}>
          <InputLabel id="evento-tipo-label">Tipo</InputLabel>
          <Select labelId="evento-tipo-label" label="Tipo" value={datos.tipo} onChange={e => cambiarTipo(e.target.value)}>
            {Object.entries(TIPOS).map(([v, n]) => (
              <MenuItem key={v} value={Number(v)}>
                {n}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField size="small" type="number" label="Edición" value={datos.anio} onChange={e => set("anio")(e.target.value)} helperText="La Prueba de nov-2026 es de la 2027" />
        <TextField size="small" label="Nombre" value={datos.nombre} onChange={e => set("nombre")(e.target.value)} />
        <TextField size="small" label="Lugar" value={datos.lugar} onChange={e => set("lugar")(e.target.value)} />
        <TextField size="small" style={{ minWidth: 320 }} label="Fuente (URL)" value={datos.fuenteUrl ?? ""} onChange={e => set("fuenteUrl")(e.target.value)} />
        <TextField size="small" style={{ minWidth: 320 }} label="Fuente de los fallos (URL)" value={datos.fuenteFallosUrl ?? ""} onChange={e => set("fuenteFallosUrl")(e.target.value)} />
        <TextField size="small" style={{ minWidth: 320 }} label="Nota para el público" value={datos.nota ?? ""} onChange={e => set("nota")(e.target.value)} />
        <FormControlLabel control={<Switch checked={!!datos.publicado} onChange={e => set("publicado")(e.target.checked)} />} label="Publicado (visible en el app)" />
      </div>
      <div className="flex gap-2 mt-3">
        <Button variant="contained" onClick={guardar}>
          Guardar
        </Button>
        {onCancelar && <Button onClick={onCancelar}>Cancelar</Button>}
        {onEliminar && (
          <Button color="error" onClick={onEliminar}>
            Eliminar evento
          </Button>
        )}
      </div>
    </section>
  );
};

const Avisos = ({ errores = [], advertencias = [] }) => (
  <>
    {errores.length > 0 && (
      <ul className="text-red-600 my-2" aria-label="Errores">
        {errores.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    )}
    {advertencias.length > 0 && (
      <ul className="text-amber-600 my-2" aria-label="Advertencias">
        {advertencias.map((a, i) => (
          <li key={i}>{a}</li>
        ))}
      </ul>
    )}
  </>
);

const Conjunto = ({ fila }) =>
  fila.conjuntoId ? (
    <span>
      #{fila.conjuntoId} {fila.conjuntoNombre}
    </span>
  ) : (
    <Chip size="small" color={fila.cruce === "ambiguo" ? "warning" : "default"} label={fila.cruce === "ambiguo" ? "Ambiguo" : "Sin conjunto"} />
  );

// Programación: texto pegado → vista previa → Guardar (reemplaza noches y participaciones).
const Programacion = ({ detalle, onGuardado }) => {
  const sendRequest = useRequestHandler();
  const dispatch = useDispatch();
  const [texto, setTexto] = useState("");
  const [previa, setPrevia] = useState(null);
  const actual = detalle.programacion;

  const previsualizar = () =>
    sendRequest(Servicios.PrevisualizarProgramacionEvento, [detalle.id, texto], r => setPrevia(r?.data ?? null));

  const guardar = () => {
    if (!window.confirm("¿Guardar? Reemplaza toda la programación cargada de este evento.")) return;
    sendRequest(Servicios.GuardarProgramacionEvento, [detalle.id, previa], r => {
      dispatch(showSuccessMessage("Programación guardada"));
      setPrevia(null);
      setTexto("");
      onGuardado(r?.data);
    });
  };

  const filas = p => [...p.noches.flatMap(n => n.participaciones.map(x => ({ ...x, noche: `${n.nombre} · ${n.fecha ?? "sin fecha"}` }))), ...p.sinNoche.map(x => ({ ...x, noche: "Sin noche" }))];

  return (
    <section aria-label="Programación" className="border border-gray-200 dark:border-gray-700 rounded p-4">
      <h5 className="page-title mb-1">Programación</h5>
      <p className="text-sm text-gray-500 mb-2">
        Cargada: {actual.noches.length} noches, {filas(actual).length} participantes. Una línea de encabezado por noche y una
        por participante; la hora y la categoría son opcionales.
      </p>
      <TextField
        multiline
        fullWidth
        minRows={6}
        label="Pegá la programación"
        placeholder={AYUDA_PROGRAMACION}
        value={texto}
        onChange={e => setTexto(e.target.value)}
      />
      <div className="flex gap-2 mt-2">
        <Button variant="outlined" onClick={previsualizar} disabled={!texto.trim()}>
          Previsualizar
        </Button>
        <Button variant="contained" onClick={guardar} disabled={!previa || previa.errores.length > 0}>
          Guardar
        </Button>
      </div>
      {previa && (
        <>
          <Avisos errores={previa.errores} advertencias={previa.advertencias} />
          <Table size="small" aria-label="Vista previa de la programación">
            <TableHead>
              <TableRow>
                <TableCell>Noche</TableCell>
                <TableCell>Hora</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>Conjunto</TableCell>
                <TableCell>Compite</TableCell>
                <TableCell>Avisos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filas(previa).map((f, i) => (
                <TableRow key={i}>
                  <TableCell>{f.noche}</TableCell>
                  <TableCell>{f.hora ?? ""}</TableCell>
                  <TableCell>{f.nombre}</TableCell>
                  <TableCell>{CATEGORIAS[f.categoriaId] ?? ""}</TableCell>
                  <TableCell>
                    <Conjunto fila={f} />
                  </TableCell>
                  <TableCell>{f.compite ? "Sí" : "No compite"}</TableCell>
                  <TableCell className="text-xs">{f.advertencias.join(" ")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </>
      )}
    </section>
  );
};

// Fallos: texto pegado → vista previa → Guardar; después «Publicar fallos» (con o sin push, o push de prueba).
const Fallos = ({ detalle, onGuardado }) => {
  const sendRequest = useRequestHandler();
  const dispatch = useDispatch();
  const [texto, setTexto] = useState("");
  const [previa, setPrevia] = useState(null);
  const [notificar, setNotificar] = useState(true);
  const [modoPrueba, setModoPrueba] = useState(true);
  const actual = detalle.fallos;

  const previsualizar = () =>
    sendRequest(Servicios.PrevisualizarFallosEvento, [detalle.id, texto], r => setPrevia(r?.data ?? null));

  const guardar = () => {
    if (!window.confirm("¿Guardar? Reemplaza todos los resultados y premios cargados de este evento.")) return;
    sendRequest(Servicios.GuardarFallosEvento, [detalle.id, previa], r => {
      dispatch(showSuccessMessage(detalle.fallosPublicados ? "Fallos guardados (ya están publicados: se ven en el app)" : "Fallos guardados (todavía no se ven en el app)"));
      setPrevia(null);
      setTexto("");
      onGuardado(r?.data);
    });
  };

  const publicar = () => {
    const aviso = !notificar
      ? "¿Publicar los fallos sin mandar push?"
      : modoPrueba
        ? "¿Mandar la push de prueba a los admins? Los fallos no se publican."
        : "¿Publicar los fallos y mandar la push a todos? La push sale una sola vez por evento.";
    if (!window.confirm(aviso)) return;
    // SEC-EV-02: «Modo prueba» solo vale con push (el checkbox queda tildado aunque esté deshabilitado).
    sendRequest(Servicios.PublicarFallosEvento, [detalle.id, notificar, notificar && modoPrueba], r => {
      const res = r?.data;
      const mensaje = `${PUSH[res?.push] ?? "Listo"}${res?.destinatarios ? ` (${res.destinatarios} cuentas)` : ""}`;
      dispatch(res?.push === "ya_enviada" ? showInfoMessage(mensaje) : showSuccessMessage(mensaje));
      onGuardado();
    });
  };

  const ocultar = () => {
    if (!window.confirm("¿Ocultar los fallos del app? Volver a publicarlos no repite la push.")) return;
    sendRequest(Servicios.OcultarFallosEvento, [detalle.id], () => onGuardado());
  };

  return (
    <section aria-label="Fallos" className="border border-gray-200 dark:border-gray-700 rounded p-4">
      <h5 className="page-title mb-1">Fallos</h5>
      <p className="text-sm text-gray-500 mb-2">
        Cargados: {actual.resultados.length} resultados y {actual.premios.length} premios.{" "}
        {detalle.fallosPublicados ? <Chip size="small" color="success" label="Publicados" /> : <Chip size="small" label="Sin publicar" />}{" "}
        {detalle.fechaPushFallos && <Chip size="small" variant="outlined" label={`Push enviada ${detalle.fechaPushFallos.replace("T", " ").slice(0, 16)}`} />}
      </p>
      <p className="text-sm text-gray-500 mb-2">
        Un encabezado por categoría (# Murgas). En la Prueba de Admisión, sin marca cuenta como «clasificó»; «# Murgas · No
        clasificados» lo cambia. Después de «# Premios», «Premio: Ganador» por línea.
      </p>
      <TextField
        multiline
        fullWidth
        minRows={6}
        label="Pegá los fallos"
        placeholder={AYUDA_FALLOS}
        value={texto}
        onChange={e => setTexto(e.target.value)}
      />
      <div className="flex gap-2 mt-2">
        <Button variant="outlined" onClick={previsualizar} disabled={!texto.trim()}>
          Previsualizar
        </Button>
        <Button variant="contained" onClick={guardar} disabled={!previa || previa.errores.length > 0}>
          Guardar
        </Button>
      </div>
      {previa && (
        <>
          <Avisos errores={previa.errores} advertencias={previa.advertencias} />
          <Table size="small" aria-label="Vista previa de los resultados">
            <TableHead>
              <TableRow>
                <TableCell>Categoría</TableCell>
                <TableCell>Pos.</TableCell>
                <TableCell>Nombre</TableCell>
                <TableCell>Puntaje</TableCell>
                <TableCell>Clasificó</TableCell>
                <TableCell>Conjunto</TableCell>
                <TableCell>Avisos</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {previa.resultados.map((f, i) => (
                <TableRow key={i}>
                  <TableCell>{CATEGORIAS[f.categoriaId] ?? "General"}</TableCell>
                  <TableCell>{f.posicion ?? ""}</TableCell>
                  <TableCell>{f.nombre}</TableCell>
                  <TableCell>{f.puntaje ?? ""}</TableCell>
                  <TableCell>{f.clasifico == null ? "" : f.clasifico ? "Sí" : "No"}</TableCell>
                  <TableCell>
                    <Conjunto fila={f} />
                  </TableCell>
                  <TableCell className="text-xs">{f.advertencias.join(" ")}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {previa.premios.length > 0 && (
            <Table size="small" aria-label="Vista previa de los premios" className="mt-3">
              <TableHead>
                <TableRow>
                  <TableCell>Premio</TableCell>
                  <TableCell>Ganador</TableCell>
                  <TableCell>Conjunto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {previa.premios.map((p, i) => (
                  <TableRow key={i}>
                    <TableCell>{p.premio}</TableCell>
                    <TableCell>{p.ganador}</TableCell>
                    <TableCell>{p.conjuntoId ? `#${p.conjuntoId} ${p.conjuntoNombre}` : ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      <hr className="border-gray-200 dark:border-gray-700 my-4" />
      <div className="flex flex-wrap items-center gap-3">
        <FormControlLabel control={<Checkbox checked={notificar} onChange={e => setNotificar(e.target.checked)} />} label="Mandar push" />
        <FormControlLabel
          control={<Checkbox checked={modoPrueba} disabled={!notificar} onChange={e => setModoPrueba(e.target.checked)} />}
          label="Modo prueba (solo admins, no publica)"
        />
        <Button variant="contained" color="secondary" onClick={publicar} disabled={!detalle.publicado || (actual.resultados.length === 0 && actual.premios.length === 0)}>
          Publicar fallos
        </Button>
        {detalle.fallosPublicados && <Button onClick={ocultar}>Ocultar fallos</Button>}
      </div>
      {!detalle.publicado && <p className="text-sm text-gray-500 mt-2">Publicá el evento antes de publicar los fallos.</p>}
    </section>
  );
};

export default withAuthenticationRequired(Eventos, {
  onRedirecting: () => <Loading />
});
